import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingScreen from './components/common/LoadingScreen';
import RedirectToFirstAvailable from './components/routing/RedirectToFirstAvailable';

import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ClientManagement from "./pages/ClientManagement";
import Inward from "./pages/Inward";
import DispatchCreate from "./pages/DispatchCreate";
import ProductList from "./pages/ProductList";
import SecurityCheck from "./pages/SecurityCheck";
import ClientInventoryPage from "./pages/ClientInventoryPage";
import WarehouseLocation from "./pages/WarehouseLocation";
import WarehouseManagement from "./pages/WarehouseManagement";
// import MasterData from "./pages/MasterData"; // Master Data module hidden — functionality retired
import MISReport from "./pages/MISReport";
import GrnCreate from "./pages/GrnCreate";
import GrnPastEditPage from "./pages/GrnPastEditPage";
import DispatchPastEditPage from "./pages/DispatchPastEditPage";
import GrnList from "./pages/GrnList";
import GrnView from "./pages/GrnView";
import GrnApprovals from "./pages/approvals/GrnApprovals";
import DispatchApprovals from "./pages/approvals/DispatchApprovals";
import { queryClient } from './lib/queryClient';
import InventoryPage from "./pages/InventoryPage";
import ReportsPage from "./pages/ReportsPage";
import AuditReportPage from "./pages/AuditReportPage";
import OtpVerificationPage from "./pages/OtpVerificationPage";
import TwoFactorManagementPage from "./pages/TwoFactorManagementPage";
import ProductCatalogPage from "./pages/ProductCatalogPage";
import Unauthorized from "./pages/Unauthorized";
import RolesPermissionsPage from "./pages/RolesPermissionsPage";
import UserManagementPage from "./pages/UserManagementPage";
import TenantControlPage from "./pages/TenantControlPage";
import Outward from "./pages/Outward";
import PalletAssignment from "./pages/PalletAssignment";
import BillingConfigPage from "./pages/BillingConfigPage";
import BillingPage from "./pages/BillingPage";
import InvoiceDetailPage from "./pages/InvoiceDetailPage";

// Protected Route Component
function ProtectedRoute({
  children,
  permissions,
  matchMode = 'ANY',
}: {
  children: React.ReactNode;
  permissions?: string[];
  matchMode?: 'ANY' | 'ALL';
}) {
  const { isAuthenticated, isLoading, hasPermission } = useAuth();
  
  if (isLoading) {
    return <LoadingScreen message="Checking authentication..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (permissions && permissions.length > 0) {
    const isAllowed =
      matchMode === 'ALL'
        ? permissions.every((permission) => hasPermission(permission))
        : permissions.some((permission) => hasPermission(permission));

    if (!isAllowed) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}

// App Routes Component (needs to be inside AuthProvider)
function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/verify-otp" element={<OtpVerificationPage />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/unauthorized" element={<ProtectedRoute><Unauthorized /></ProtectedRoute>} />
        <Route path="/" element={<ProtectedRoute><RedirectToFirstAvailable /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute permissions={['dashboard:view']}><Dashboard /></ProtectedRoute>} />
        <Route path="/inward" element={<ProtectedRoute permissions={['grn:view', 'grn:create', 'grn:submit', 'grn:approve']}><Inward /></ProtectedRoute>} />
        <Route path="/grn" element={<ProtectedRoute permissions={['grn:view']}><GrnList /></ProtectedRoute>} />
        <Route path="/grn-create/:gateEntryId" element={<ProtectedRoute permissions={['grn:create', 'grn:edit_past']}><GrnCreate /></ProtectedRoute>} />
        <Route path="/grn-past-edit/:grnId" element={<ProtectedRoute permissions={['grn:edit_past']}><GrnPastEditPage /></ProtectedRoute>} />
        <Route path="/grn/:id" element={<ProtectedRoute permissions={['grn:view']}><GrnView /></ProtectedRoute>} />
        <Route path="/approvals-grn" element={<ProtectedRoute permissions={['grn:approve']}><GrnApprovals /></ProtectedRoute>} />
        <Route path="/approvals-dispatch" element={<ProtectedRoute permissions={['dispatch:approve']}><DispatchApprovals /></ProtectedRoute>} />
        <Route path="/outward" element={<ProtectedRoute permissions={['dispatch:view', 'dispatch:create', 'dispatch:submit', 'dispatch:approve']}><Outward /></ProtectedRoute>} />
        <Route path="/dispatch-create/:gateEntryId" element={<ProtectedRoute permissions={['dispatch:create', 'dispatch:edit_past']}><DispatchCreate /></ProtectedRoute>} />
        <Route path="/dispatch-past-edit/:dispatchId" element={<ProtectedRoute permissions={['dispatch:edit_past']}><DispatchPastEditPage /></ProtectedRoute>} />
        <Route path="/clients" element={<ProtectedRoute permissions={['client:view']}><ClientManagement /></ProtectedRoute>} />
        <Route path="/products" element={<ProtectedRoute><ProductList /></ProtectedRoute>} />
        <Route path="/security-check" element={<ProtectedRoute permissions={['gate-entry:create']}><SecurityCheck /></ProtectedRoute>} />
        <Route path="/inventory" element={<ProtectedRoute permissions={['inventory:view']}><InventoryPage /></ProtectedRoute>} />
        <Route path="/inventory/:tab" element={<ProtectedRoute permissions={['inventory:view']}><InventoryPage /></ProtectedRoute>} />
        <Route path="/clients-inventory/:id" element={<ProtectedRoute permissions={['inventory:view']}><ClientInventoryPage /></ProtectedRoute>} />
        <Route path="/warehouse-location" element={<ProtectedRoute><WarehouseLocation /></ProtectedRoute>} />
        <Route path="/warehouse-management" element={<ProtectedRoute><WarehouseManagement /></ProtectedRoute>} />
        <Route path="/pallet-assignment" element={<ProtectedRoute><PalletAssignment /></ProtectedRoute>} />
        {/* Master Data module hidden — functionality retired */}
        {/* <Route path="/master-data" element={<ProtectedRoute><MasterData /></ProtectedRoute>} /> */}
        <Route path="/mis-report" element={<ProtectedRoute><MISReport /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute permissions={['reports:view']}><ReportsPage /></ProtectedRoute>} />
        <Route path="/audit-report" element={<ProtectedRoute permissions={['audit-report:view']}><AuditReportPage /></ProtectedRoute>} />
        <Route path="/two-factor-management" element={<ProtectedRoute permissions={['two-factor:manage']}><TwoFactorManagementPage /></ProtectedRoute>} />
        <Route path="/product-catalog" element={<ProtectedRoute permissions={['master-data:view']}><ProductCatalogPage /></ProtectedRoute>} />
        <Route path="/platform-tenants" element={<ProtectedRoute permissions={['tenants:manage', 'tenants:impersonate']}><TenantControlPage /></ProtectedRoute>} />
        <Route path="/settings-roles-permissions" element={<ProtectedRoute permissions={['roles:assign-permissions', 'permissions:view']} matchMode="ALL"><RolesPermissionsPage /></ProtectedRoute>} />
        <Route path="/settings-users" element={<ProtectedRoute permissions={['users:manage']}><UserManagementPage /></ProtectedRoute>} />
        <Route path="/billing-config" element={<ProtectedRoute permissions={['billing-config:view']}><BillingConfigPage /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute permissions={['billing:view']}><BillingPage /></ProtectedRoute>} />
        <Route path="/billing/invoices/:id" element={<ProtectedRoute permissions={['billing:form-view']}><InvoiceDetailPage /></ProtectedRoute>} />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
